/**
 * Phase 0.5 Component F — wraps a field that may be redacted per the
 * resolver's MaskedFields list.
 *
 * Usage:
 *   <MaskedField fieldName="Price" resource={contractRef}>
 *     <span>${contract.price.toLocaleString()}</span>
 *   </MaskedField>
 *
 * Render rules:
 *   - viewMode === "no_view"  → render `null` (the parent surface
 *     should also be using <NoViewSlot>).
 *   - viewMode === "masked", OR the field is listed in MaskedFields
 *     under "simplified" (the resolver emits the mask list for both
 *     modes) → render the redaction placeholder. The placeholder carries
 *     `data-view-mode` so Masked and Simplified are distinguishable in the
 *     DOM (a Simplified gate is "you lack the role"; a Masked gate is
 *     "this value is hidden") even though both read "Restricted".
 *   - otherwise → render children unchanged.
 *
 * Field-name matching is permissive: both Pascal/CamelCase ("Price")
 * and snake_case ("base_value") are accepted to match the resolver's
 * Go-side or proto-side field naming.
 */ import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { fieldIsMasked } from './types';
import { useViewProjection } from './useViewProjection';
const DEFAULT_PLACEHOLDER_CLASS = 'text-muted-foreground italic text-sm select-none';
export function MaskedField({ fieldName, resource, action = 'read', placeholder = 'Restricted', placeholderClassName = DEFAULT_PLACEHOLDER_CLASS, children }) {
    const { data } = useViewProjection(resource, {
        action
    });
    if (!data) {
        // Loading or fail-closed — render placeholder rather than the raw
        // children, fail-closed.
        return /*#__PURE__*/ _jsx("span", {
            className: placeholderClassName,
            "data-testid": Testids.maskedField.placeholder,
            "data-view-mode": "loading",
            children: placeholder
        });
    }
    if (data.viewMode === 'no_view') return null;
    // Masked always redacts; Simplified redacts the fields the resolver lists
    // (it emits maskedFields for both modes) — so a Simplified-gated sensitive
    // field shows the placeholder instead of leaking the server-zeroed child.
    if (!fieldIsMasked(data.viewMode, data.maskedFields, fieldName)) return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
    return /*#__PURE__*/ _jsx("span", {
        className: placeholderClassName,
        "data-testid": Testids.maskedField.placeholder,
        "data-view-mode": data.viewMode,
        children: placeholder
    });
}
